/**
 * Questions component
 *
 * Description:
 * Manages and renders a quiz flow, displaying questions and handling navigation, answer submission, and feedback.
 *
 * Features:
 * - Displays current question and progress.
 * - Renders different question types (Radio, Check, Input).
 * - Handles navigation between questions.
 * - Submits answers and manages quiz completion.
 * - Shows feedback and loading states.
 * - Uses Material-UI for UI elements.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import {
  evaluateApplication,
  feedback,
  finishApplication,
  passQuiz,
  saveUpdateAnswer
} from 'store/lesson-actions';

import Loading from 'components/UI/Loading/Loading';
import RadioQuestion from './RadioQuestion/RadioQuestion';
import CheckQuestion from './CheckQuestion/CheckQuestion';
import InputQuestion from './InputQuestion/InputQuestion';
import Timer from './Timer/Timer';
import classes from './Questions.module.css';

const Questions = props => {
  const { t } = useTranslation(['lesson']);
  const dataAcademy = useSelector(state => state.academyInfo);
  // Local state for loading, navigation, and current question
  const [loading, setLoading] = useState(false);
  const [nextQuestionBtn, setNexQuestionBtn] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  /**
   * Handles navigation between questions.
   */
  const changeQuestionHandler = nextQuestion => () => {
    setCurrentQuestion(currentQuestion + nextQuestion);
    setNexQuestionBtn(false);
  };

  /**
   * Enables the next question button.
   */
  const nextQuestionHandler = () => {
    setNexQuestionBtn(true);
  };

  /**
   * Handles quiz completion, evaluation, and feedback.
   */
  const finishQuizHandler = () => {
    setLoading(true);
    (async () => {
      const finishQuiz = await finishApplication(
        props.idApplication,
        dataAcademy.data.shortName
      );
      if (finishQuiz) {
        const evaluate = await evaluateApplication(
          props.idApplication,
          dataAcademy.data.shortName,
          dataAcademy.data.shortName,
          100
        );
        if (evaluate) {
          const feedbackData = await feedback(
            props.idQuiz,
            evaluate.score
          );
          if (feedbackData) {
            const calification = await passQuiz(props.idApplication);
            if (calification) {
              if (!calification.autoEvaluate) {
                props.changeQuizStatus(2);
              } else {
                if (calification.pass) {
                  props.changeQuizStatus(2);
                } else {
                  props.changeQuizStatus(1);
                }
              }
              props.showFeedback(
                feedbackData,
                calification,
                evaluate
              );
            }
          }
        }
      }
    })();
  };

  /**
   * Handles automatic submission when timer expires:
   * 1. Filters out questions that haven't been answered (preguntas faltantes).
   * 2. Saves blank/default answers only for missing questions (without re-sending answered ones).
   * 3. Triggers finish, evaluation, and feedback flow.
   */
  const handleTimeUp = () => {
    setLoading(true);
    (async () => {
      const missingQuestions = props.questions.filter(question => {
        if (question.choices && question.choices.length > 0) {
          return !question.choices.some(choice =>
            Boolean(choice.answer)
          );
        }
        return !question.userAnswer && !question.answer;
      });

      for (const question of missingQuestions) {
        try {
          await saveUpdateAnswer(
            props.idApplication,
            null,
            false,
            '',
            question.idQuestion
          );
        } catch (error) {
          console.error(
            'Error saving missing question answer:',
            error
          );
        }
      }

      finishQuizHandler();
    })();
  };

  return (
    <>
      {/* Custom prop: 'className' for quiz container styling */}
      <div className={classes.quizContainer}>
        <div className={classes.headerContainer}>
          {props.time > 0 ? (
            <Timer time={props.time} onTimeUp={handleTimeUp} />
          ) : (
            <div />
          )}
          <p className={classes.questionsInfo}>
            {t('reagent')}:{' '}
            <span className={classes.question}>
              {currentQuestion}
            </span>{' '}
            / {props.questions.length}
          </p>
        </div>
        {props.questions.map(
          question =>
            question.order === currentQuestion &&
            (question.type === 1 ? (
              <RadioQuestion
                key={question.idQuestion}
                // Custom prop: 'questionData' passes question info
                questionData={question}
                // Custom prop: 'idApplication' passes application ID
                idApplication={props.idApplication}
                // Custom prop: 'currentQuestion' passes current question index
                currentQuestion={currentQuestion}
                // Custom prop: 'nextQuestion' passes handler for next question
                nextQuestion={nextQuestionHandler}
              />
            ) : question.type === 9 ? (
              <CheckQuestion
                key={question.idQuestion}
                questionData={question}
                idApplication={props.idApplication}
                currentQuestion={currentQuestion}
                nextQuestion={nextQuestionHandler}
              />
            ) : (
              <InputQuestion
                key={question.idQuestion}
                questionData={question}
                idApplication={props.idApplication}
                currentQuestion={currentQuestion}
                nextQuestion={nextQuestionHandler}
              />
            ))
        )}
      </div>
      {/* Custom prop: 'className' for button container styling */}
      <aside className={classes.btnsContainer}>
        {!loading ? (
          <>
            <Button
              // Custom prop: 'onClick' for navigation/back
              onClick={
                currentQuestion > 1
                  ? changeQuestionHandler(-1)
                  : props.changeView
              }
              className={classes.nextBtn}
            >
              <ChevronLeftIcon /> {t('previous')}
            </Button>
            <Button
              disabled={!nextQuestionBtn}
              // Custom prop: 'onClick' for next/save
              onClick={
                currentQuestion === props.questions.length
                  ? finishQuizHandler
                  : changeQuestionHandler(1)
              }
              className={classes.nextBtn}
            >
              {currentQuestion === props.questions.length
                ? t('save')
                : t('next')}{' '}
              <ChevronRightIcon />
            </Button>
          </>
        ) : (
          <Loading />
        )}
      </aside>
    </>
  );
};

Questions.propTypes = {
  /**
   * Function to change the view (e.g., go back)
   */
  changeView: PropTypes.func,
  /**
   * Array of question objects for the quiz
   */
  questions: PropTypes.array,
  /**
   * Application ID for answer submission
   */
  idApplication: PropTypes.number,
  /**
   * Quiz ID for feedback
   */
  idQuiz: PropTypes.number,
  /**
   * Function to show feedback after quiz completion
   */
  showFeedback: PropTypes.func,
  /**
   * Function to change quiz status
   */
  changeQuizStatus: PropTypes.func,
  time: PropTypes.number
};

Questions.defaultProps = {
  changeView: undefined,
  questions: [],
  idApplication: undefined,
  idQuiz: undefined,
  showFeedback: undefined,
  changeQuizStatus: undefined,
  time: 0
};

export default Questions;
